/**
 * SOLID Review
 * S — Good. One concern: the account's credits — the checkout, the balance read after a purchase, the
 *     pushed balance, and whose push to hear — each as at most one server call around a decision.
 * O — Good. A new payment act is one more shell of the same shape.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the payment ops of `ClientUseCases`, and the delivery of `creditsUpdated`.
 * D — Strong. Three port interfaces and the shared steps, all injected.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  IEntityCheckout, IEntityBalance, IPayloadCreditsUpdated, ICommandCreditsChanged, ICommandBalanceRead,
} from '../../domain/ports/schemas/PaymentMoves';
import type { IPortClientPaymentServer, IPortClientServerWatch } from '../ports/contracts';
import { DONE, type ClientSteps, type TOutcome, type TResult } from '../shared/ClientSteps';

export interface IConfigDepPaymentContext {
  readonly decisions: IPortClientDecisions;
  readonly payments: IPortClientPaymentServer;
  /** The account watch — the pushed balance's subscription. */
  readonly watch: IPortClientServerWatch;
  /** The one serial section, shared with every other context. */
  readonly steps: ClientSteps;
}

const NOTHING: ICommandNothing = {};

/**
 * THE PAYMENT SIDE'S SHELLS (frontend strict-ports plan, FE4c, 2026-10-04). BuyCreditsButton called the
 * legacy auth adapter's checkout from React; PaymentSuccessScreen polled its `getMe` and wrote both stores;
 * AppRoot subscribed to the pushed balance with `useCreditsUpdated` and wrote both stores again. The calls
 * are here now, and where a balance is kept is one decision (`credits.ts`).
 */
export class PaymentContext {
  constructor(private readonly deps: IConfigDepPaymentContext) {}

  async startCheckout(ctx: IContextRequest, _command: ICommandNothing): Promise<TResult<IEntityCheckout>> {
    return this.deps.payments.createCheckout(ctx);
  }

  /** One read of the account's balance, kept; a failed read changes nothing — the screen retries. */
  async readBalance(ctx: IContextRequest, _command: ICommandNothing): Promise<TResult<IEntityBalance>> {
    const { decisions, steps, payments } = this.deps;
    const read = await payments.balance(ctx);
    if (!read.success) return read;
    const command: ICommandBalanceRead = { credits: read.value.matchCredits };
    const kept = await steps.session(ctx, (input) => decisions.balanceRead(ctx, input, command));
    if (!kept.success) return kept;
    return read;
  }

  /** Hear the seated player's pushed balance — or nobody's, when no one is seated. */
  async watchAccount(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, steps, watch } = this.deps;
    const target = await steps.session(ctx, (input) => decisions.planAccountWatch(ctx, input, NOTHING));
    if (!target.success) return target;
    return watch.watchAccount(ctx, target.value);
  }

  async unwatchAccount(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    return this.deps.watch.unwatchAccount(ctx);
  }

  /** The server pushed a balance (delivered): kept if it is this tab's account's or seated player's. */
  async creditsUpdated(ctx: IContextRequest, payload: IPayloadCreditsUpdated): Promise<TOutcome> {
    const { decisions, steps } = this.deps;
    const changed: ICommandCreditsChanged = { playerId: payload.playerId, credits: payload.newCredits };
    const kept = await steps.session(ctx, (input) => decisions.creditsChanged(ctx, input, changed));
    return kept.success ? DONE : kept;
  }
}
